import * as React from 'react';
import styled from 'styled-components';

const ProgressContainer = styled.div`
  width: 100%;
  padding: 30px 0;
  margin-bottom: 20px;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  max-width: 600px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;
  }
`;

const ProgressBar = styled.div<{progress: number; cor: string}>`
  position: absolute;
  top: 20px;
  left: 20px;
  height: 2px;
  background: ${(props) => props.cor};
  /* A trilha vai do centro do primeiro circulo ao centro do ultimo, por isso o
     recuo de 20px (metade do circulo) nas duas pontas. */
  width: calc((100% - 40px) * ${(props) => props.progress});
  transition: width 0.3s ease;
  z-index: 0;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const StepCircle = styled.div<{active: boolean; completed: boolean; cor: string}>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(props) => (props.completed || props.active ? props.cor : '#e0e0e0')};
  color: ${(props) => (props.completed || props.active ? '#fff' : '#333')};
  font-weight: bold;
  transition: all 0.3s ease;
  box-shadow: ${(props) => (props.active ? `0 0 0 4px ${props.cor}33` : 'none')};

  svg {
    width: 20px;
    height: 20px;
  }

  span {
    font-size: 14px;
    font-weight: 600;
  }
`;

const StepLabel = styled.div<{active: boolean}>`
  font-size: 12px;
  font-weight: ${(props) => (props.active ? '600' : '400')};
  color: ${(props) => (props.active ? '#333' : '#999')};
  text-align: center;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

// Sem Materialize: a pagina de cadastro nao carrega material-icons, entao o
// "concluido" precisa de um icone que venha no proprio bundle.
const IconeConcluido = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface ProgressStep {
  key: string;
  label: string;
}

const STEPS_CHECKOUT: ProgressStep[] = [
  {key: 'plan', label: 'Plano'},
  {key: 'personal', label: 'Dados'},
  {key: 'payment', label: 'Pagamento'},
  {key: 'confirmation', label: 'Confirmação'},
];

interface ProgressIndicatorProps {
  currentStep: string;
  /** Sem valor, usa os passos do checkout — que era o unico uso ate aqui. */
  steps?: ProgressStep[];
  ariaLabel?: string;
  colors?: {primary?: string};
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  currentStep,
  steps = STEPS_CHECKOUT,
  ariaLabel = 'Progresso',
  colors,
}) => {
  const cor = colors?.primary || '#006bf3';
  const atual = Math.max(0, steps.findIndex((step) => step.key === currentStep));
  // Entre os centros do primeiro e do ultimo circulo: com (atual+1)/total a
  // barra passava do primeiro circulo ja no comeco e nunca batia com eles.
  const progresso = steps.length > 1 ? atual / (steps.length - 1) : 1;

  return (
    <ProgressContainer role="group" aria-label={ariaLabel}>
      <StepsContainer>
        <ProgressBar progress={progresso} cor={cor} />
        {steps.map((step, index) => {
          const ativo = index === atual;
          const concluido = index < atual;

          return (
            <Step key={step.key} aria-current={ativo ? 'step' : undefined}>
              <StepCircle active={ativo} completed={concluido} cor={cor}>
                {concluido ? <IconeConcluido /> : <span>{index + 1}</span>}
              </StepCircle>
              {/* O estado tambem vai em texto: cor sozinha nao comunica a quem
                  usa leitor de tela nem a quem nao distingue as cores. */}
              <StepLabel active={ativo}>
                {step.label}
                <span className="sr-only">
                  {concluido ? ' (concluído)' : ativo ? ' (etapa atual)' : ''}
                </span>
              </StepLabel>
            </Step>
          );
        })}
      </StepsContainer>
    </ProgressContainer>
  );
};
